// src-lit/components/product/kd-cart-add-form.ts
import { LitElement, nothing } from 'lit';
import { customElement } from 'lit/decorators.js';
import { SELECTORS } from '../../constants/selectors';
import { isPantryMixedCart } from '../cart/pantry.logic';
import {
  type CartApiErrorResponse,
  type CartState,
  buildAddPayload,
  buildChangePayload,
  cartHasPantryBox,
  computeTotalDonation,
  computeTotalQuantity,
  extractAnyGiftProperties,
  findExistingLine,
  mergeProperties,
  parseCartApiError,
} from './cart-add-form.logic';

const SUPPORT_GROUP_KEY = '支援先団体';
const SUPPORT_AMOUNT_KEY = '支援金額';
const ERROR_GENERIC = 'カートへの追加に失敗しました。';
const BLOCK_ADD_CLASS = 'blockAdd';
// プレミアム会員登録商品がカートにある状態で他商品を追加しようとしたときのメッセージ
// （v1 main-product.js:16-23 のモーダル文言相当。v2 は main-product.js 非読込のため Lit で表示する）。
const BLOCK_ADD_MESSAGE = 'プレミアム会員登録と商品の決済を同時に行うことはできません。';
// カートに えらんでBOX（pantry）がある状態で通常商品を追加しようとしたときのメッセージ。
// v1 は product-form.js がページロード時にモーダルで警告（非ブロッキング）していたが、v2 では
// その導線が失われていた（退行）。ここで通常商品の追加自体を阻止し、理由を明示する（ハードブロック化）。
const PANTRY_MIX_MESSAGE =
  '「えらんでKuradashi」は他の商品と同時購入できません。カートを空にしてからお試しください。';
// 送信中を表す状態クラス。二重送信の同期再入ガードの判定キーでもある。
// v1 は `.product-form__submit.loading`（base.css の `.button.loading`）でスピナーが出ていたが、
// v2 のボタンは `button` クラスを持たないため CSS 側は効かない。見た目の切替は
// setSubmitLoading が data-cart-submit-content / data-cart-submit-spinner の display で行う。
const LOADING_CLASS = 'loading';
const CART_FETCH_URL = '/cart.js';
const CART_PAGE_URL = '/cart';
const FALLBACK_ADD_URL = '/cart/add';
const FALLBACK_CHANGE_URL = '/cart/change';
const CART_POST_HEADERS = {
  'Content-Type': 'application/json',
  Accept: 'application/javascript',
} as const;

interface ShopifyProductGlobal {
  id: number;
  price: number;
  type: string;
  variant?: { id: number };
}

declare global {
  interface Window {
    routes?: {
      cart_add_url?: string;
      cart_change_url?: string;
    };
    Shopify?: { product?: ShopifyProductGlobal };
    sendYappliEvent?: (
      eventName: string,
      params: Record<string, string>,
    ) => void;
  }
}

/**
 * @element kd-cart-add-form
 * @description 通常商品のカート追加 submit ハンドラ（v1 `<product-form>` の Lit 移行版、#3088）。
 *   - submit 時に `/cart.js` で同一バリアントを検索し、既存あれば `/cart/change`、なければ `/cart/add` に POST
 *   - properties に 支援先団体 / 支援金額（= 単価 × 合計数量） / 賞味期限 / 内容量 / AnyGift を詰める
 *   - 成功時に Yappli へ add_to_cart イベントを送り `/cart` へフルリロード遷移
 *   - エラー時はフォームに留まり `[data-cart-error-message]` にメッセージを表示
 *   - ホストに `blockAdd` クラス（プレミアム会員登録商品との同時購入制限）があるときは
 *     preventDefault で追加を阻止し、`[data-cart-error]` に理由を表示して早期リターンする。
 *     v1 は `main-product.js:16-23` の jQuery `.blockAdd form.form` ハンドラがモーダル表示を
 *     担っていたが、v2（layout: v2-theme）は main-product.js を読み込まないため Lit 側で
 *     フィードバックを出す（無反応の silent no-op を防ぐ）。
 *
 *   HTML は Liquid（v2-cart-button-regular.liquid）が生成し、本コンポーネントは振る舞いのみ。
 */
@customElement('kd-cart-add-form')
export class KdCartAddForm extends LitElement {
  createRenderRoot() {
    return this;
  }

  private form: HTMLFormElement | null = null;

  render() {
    return nothing;
  }

  connectedCallback(): void {
    super.connectedCallback();
    this.form = this.querySelector<HTMLFormElement>(
      SELECTORS.CART_ADD_FORM.FORM,
    );
    this.form?.addEventListener('submit', this.onSubmit);
    this.releasePendingSubmits();
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', this.releasePendingSubmits, {
        once: true,
      });
    }
  }

  disconnectedCallback(): void {
    super.disconnectedCallback();
    this.form?.removeEventListener('submit', this.onSubmit);
    document.removeEventListener('DOMContentLoaded', this.releasePendingSubmits);
    this.form = null;
  }

  /**
   * Lit 起動待ちで disabled にしていたボタンを有効化する。
   *
   * 起動前に押されるとネイティブ submit になり line item properties（支援金額など）が
   * 付かず、同一 variant が別ラインに分かれるため、Liquid は販売可能時も disabled +
   * data-cart-submit-pending で描画する。売り切れは pending を持たないので disabled のまま。
   * SP 固定フッター（form= 委譲）が後からパースされるケースに備え DOMContentLoaded でも再実行する。
   */
  private releasePendingSubmits = (): void => {
    if (!this.form) return;
    for (const button of this.getSubmitButtons()) {
      if (!button.matches(SELECTORS.PRODUCT_CART.SUBMIT_PENDING)) continue;
      button.removeAttribute('data-cart-submit-pending');
      button.disabled = false;
    }
  };

  private onSubmit = async (evt: Event): Promise<void> => {
    evt.preventDefault();
    if (this.classList.contains(BLOCK_ADD_CLASS)) {
      // プレミアム会員登録商品との同時購入制限。preventDefault で追加自体は阻止済みだが、
      // v2 は v1 main-product.js のモーダルを読み込まないため、ここで理由をフィードバックする
      // （無反応だとユーザーがなぜ追加できないか分からない）。
      this.showError(BLOCK_ADD_MESSAGE);
      return;
    }
    if (this.isPantryMixBlocked()) {
      // えらんでBOX がカートにある状態での通常商品追加を阻止（混在ハードブロック）。
      this.showError(PANTRY_MIX_MESSAGE);
      return;
    }

    const submitButtons = this.getSubmitButtons();
    if (
      submitButtons.length === 0 ||
      submitButtons.some((button) => button.classList.contains(LOADING_CLASS))
    ) {
      return;
    }

    this.hideError();
    this.setSubmitLoading(submitButtons, true);

    try {
      const formData = this.collectFormData();
      if (!formData) throw new Error(ERROR_GENERIC);

      const cart = await this.fetchCart();
      // えらんでBOX 混在の submit 時リアルタイム再確認（server の data-cart-has-pantry が
      // 別タブ追加等で陳腐化していても、ここで最新カートを見てブロックする）。
      if (cartHasPantryBox(cart)) {
        this.showError(PANTRY_MIX_MESSAGE);
        this.setSubmitLoading(submitButtons, false);
        return;
      }
      const existing = findExistingLine(
        cart,
        formData.variantId,
        formData.sellingPlan,
      );
      const totalQuantity = existing
        ? computeTotalQuantity(existing.quantity, formData.quantity)
        : formData.quantity;
      const totalDonation = computeTotalDonation(
        formData.donationPerUnit,
        totalQuantity,
      );
      const properties = mergeProperties({
        anyGift: formData.anyGiftProperties,
        supportGroup: formData.supportGroup,
        totalDonation,
        expirationDate: formData.expirationDate,
        volume: formData.volume,
        supportGroupKey: SUPPORT_GROUP_KEY,
        supportAmountKey: SUPPORT_AMOUNT_KEY,
      });
      const sectionsUrl = window.location.pathname;

      if (existing) {
        await this.changeCart(
          buildChangePayload({
            line: existing.line,
            totalQuantity,
            properties,
            sectionsUrl,
            sellingPlan: formData.sellingPlan,
          }),
        );
      } else {
        await this.addCart(
          buildAddPayload({
            variantId: formData.variantId,
            quantity: formData.quantity,
            properties,
            sectionsUrl,
            sellingPlan: formData.sellingPlan,
          }),
        );
      }

      this.sendYappli(totalQuantity);
      window.location.href = CART_PAGE_URL;
    } catch (error) {
      const message =
        error instanceof Error && error.message ? error.message : ERROR_GENERIC;
      this.showError(message);
      this.setSubmitLoading(submitButtons, false);
    }
  };

  /**
   * えらんでKuradashi 混在ブロック判定。
   * 本フォームは通常商品専用（子・BOX では Liquid 側で描画されない）ため addingIsPantry=false 固定。
   * カートに えらんでBOX（pantry）があるかは Liquid が `data-cart-has-pantry` で渡す。
   * 判定ロジックは pantry.logic.isPantryMixedCart に集約（SSoT）。cartHasNormal は addingIsPantry=false の
   * 分岐では未使用のため false 固定で渡す。
   */
  private isPantryMixBlocked(): boolean {
    const cartHasPantry = this.dataset.cartHasPantry === 'true';
    return isPantryMixedCart(cartHasPantry, false, false);
  }

  private collectFormData(): {
    variantId: number;
    quantity: number;
    expirationDate: string;
    volume: string;
    supportGroup: string;
    donationPerUnit: number;
    anyGiftProperties: Record<string, string>;
    sellingPlan?: number;
  } | null {
    if (!this.form) return null;

    const variantIdInput = this.querySelector<HTMLInputElement>(
      SELECTORS.PRODUCT_CART.VARIANT_ID_INPUT,
    );
    // 数量入力は <kd-cart-add-form> の外側（kd-product-cart 配下）に配置されるため、
    // 共通祖先である kd-product-cart 経由でスコープを絞って取得する。
    const productCart = this.closest('kd-product-cart');
    const quantityInput = productCart?.querySelector<HTMLInputElement>(
      SELECTORS.PRODUCT_CART.QUANTITY_INPUT,
    );
    if (!variantIdInput?.value || !quantityInput?.value) return null;

    const variantId = Number(variantIdInput.value);
    const quantity = Number(quantityInput.value);
    if (!Number.isFinite(variantId) || !Number.isFinite(quantity)) return null;

    // 支援先 select / 支援金額 hidden は donation-picker block（中央カラム）配下にあり
    // kd-product-cart（右カラム）と兄弟関係。PDP は 1 product/page なので document スコープで取得する
    // （kd-pantry-cart-add の readSupportGroup と同方針）。
    const supportSelect = document.querySelector<HTMLSelectElement>(
      SELECTORS.CART_ADD_FORM.DONATION_SELECT,
    );
    const donationInput = document.querySelector<HTMLInputElement>(
      SELECTORS.CART_ADD_FORM.DONATION_AMOUNT_INPUT,
    );
    // AnyGift inputs は SDK が body 直下に注入するため document スコープで収集する（v1 互換）
    const anyGiftInputs = Array.from(
      document.querySelectorAll<HTMLInputElement>(
        SELECTORS.ANY_GIFT.PROPERTIES_INPUTS,
      ),
    );

    // 販売プラン選択（#3485）: Huckleberry app block の widget は product form の外（カラム配下）
    // に配置され、選択値を name="selling_plan" の hidden input で React 経由出力する。
    // PDP の各 product form 用に複数（PC/SP）出力されるが、SDK が同じ値で同期するので
    // 最初に見つかった非空の value を採用する。単発購入の value="" は未指定扱い。
    const sellingPlanInputs = document.querySelectorAll<HTMLInputElement>(
      SELECTORS.CART_ADD_FORM.SELLING_PLAN_INPUT,
    );
    const sellingPlanRaw =
      Array.from(sellingPlanInputs)
        .map((el) => el.value)
        .find((v) => v !== '') ?? '';
    const sellingPlanNum =
      sellingPlanRaw === '' ? undefined : Number(sellingPlanRaw);
    const sellingPlan =
      typeof sellingPlanNum === 'number' && Number.isFinite(sellingPlanNum)
        ? sellingPlanNum
        : undefined;

    return {
      variantId,
      quantity,
      expirationDate: this.form.dataset.expirationDate ?? '',
      volume: this.form.dataset.volume ?? '',
      supportGroup: supportSelect?.value ?? '',
      donationPerUnit: donationInput
        ? Number(donationInput.value) || 0
        : 0,
      anyGiftProperties: extractAnyGiftProperties(anyGiftInputs),
      sellingPlan,
    };
  }

  private async fetchCart(): Promise<CartState> {
    const response = await fetch(CART_FETCH_URL);
    return (await response.json()) as CartState;
  }

  private async addCart(payload: object): Promise<void> {
    await this.postCart(this.cartAddUrl(), payload);
  }

  private async changeCart(payload: object): Promise<void> {
    await this.postCart(this.cartChangeUrl(), payload);
  }

  /**
   * Cart Ajax API への POST 共通処理。
   * レスポンス body の `status` フィールド（HTTP ステータスではない）が truthy なら
   * Shopify が返したエラー扱いとして `description` を throw する（v1 互換）。
   */
  private async postCart(url: string, payload: object): Promise<void> {
    const response = await fetch(url, {
      method: 'POST',
      headers: CART_POST_HEADERS,
      body: JSON.stringify(payload),
    });
    const data = (await response.json()) as CartApiErrorResponse;
    if (data.status) {
      throw new Error(parseCartApiError(data, ERROR_GENERIC));
    }
  }

  private cartAddUrl(): string {
    return window.routes?.cart_add_url ?? FALLBACK_ADD_URL;
  }

  private cartChangeUrl(): string {
    return window.routes?.cart_change_url ?? FALLBACK_CHANGE_URL;
  }

  private sendYappli(quantity: number): void {
    if (typeof window.sendYappliEvent !== 'function') return;
    const product = window.Shopify?.product;
    if (!product) return;
    // 計測失敗はカート追加成功フロー（/cart 遷移）の妨げにならないよう握りつぶす。
    // sendYappliEvent / Yappli SDK 側で例外が出ると onSubmit の catch が
    // showError('カートへの追加に失敗しました。') を実行してしまうため。
    try {
      window.sendYappliEvent('add_to_cart', {
        price: product.price.toString(),
        content_id: `${product.id}-${product.variant?.id ?? ''}`,
        content_type: product.type,
        currency: 'JPY',
        quantity: quantity.toString(),
      });
    } catch {
      // no-op: 計測失敗はユーザーには影響させない
    }
  }

  /**
   * このフォームに送信するカート追加ボタンを全て集める。
   *
   * SP の主 CTA（snippets/v2-product-sp-fixed-footer）は独自フォームを持たず
   * `form="product-form-{section_id}"` で本体フォームへ delegate するため、
   * `<kd-cart-add-form>` の配下には存在しない。配下だけを見ていると SP では
   * ローディング表示が一切出ないので、form owner（`button.form`）が本フォームと
   * 一致するボタンを document から集める。`form` 属性を持たない配下のボタンも
   * form owner は同じフォームになるため、この 1 条件で両方を拾える。
   *
   * 同一ページに複数の product form（v2_product_main / v2_product_content）があっても
   * form owner が異なるため混線しない。
   */
  private getSubmitButtons(): HTMLButtonElement[] {
    const scoped = Array.from(
      this.querySelectorAll<HTMLButtonElement>(SELECTORS.PRODUCT_CART.SUBMIT),
    );
    if (!this.form) return scoped;
    const owned = Array.from(
      document.querySelectorAll<HTMLButtonElement>(SELECTORS.PRODUCT_CART.SUBMIT),
    ).filter((button) => button.form === this.form);
    // form owner で 1 つも取れない異常時（form 属性の id 不一致など）は配下だけにフォールバックする。
    return owned.length > 0 ? owned : scoped;
  }

  /**
   * カート追加ボタンの送信中表示を切り替える。
   *
   * `loading` クラスは二重送信ガードの判定キー、`aria-busy` は支援技術向け。
   * 見た目は content（アイコン + ラベル）と spinner の display 切替で行う
   * （tw: クラスの初期値に依存せず確実に切り替わる。v2-favorite-button と同方式）。
   */
  private setSubmitLoading(buttons: HTMLButtonElement[], loading: boolean): void {
    buttons.forEach((button) => {
      if (loading) {
        button.setAttribute('aria-disabled', 'true');
        button.setAttribute('aria-busy', 'true');
        button.classList.add(LOADING_CLASS);
      } else {
        button.removeAttribute('aria-disabled');
        button.removeAttribute('aria-busy');
        button.classList.remove(LOADING_CLASS);
      }
      const content = button.querySelector<HTMLElement>(
        SELECTORS.PRODUCT_CART.SUBMIT_CONTENT,
      );
      const spinner = button.querySelector<HTMLElement>(
        SELECTORS.PRODUCT_CART.SUBMIT_SPINNER,
      );
      if (content) content.style.display = loading ? 'none' : 'flex';
      if (spinner) spinner.style.display = loading ? 'flex' : 'none';
    });
  }

  private showError(message: string): void {
    const wrapper = this.querySelector<HTMLElement>(
      SELECTORS.CART_ADD_FORM.ERROR,
    );
    const text = this.querySelector<HTMLElement>(
      SELECTORS.CART_ADD_FORM.ERROR_MESSAGE,
    );
    if (!wrapper || !text) return;
    text.textContent = message;
    wrapper.hidden = false;
  }

  private hideError(): void {
    const wrapper = this.querySelector<HTMLElement>(
      SELECTORS.CART_ADD_FORM.ERROR,
    );
    const text = this.querySelector<HTMLElement>(
      SELECTORS.CART_ADD_FORM.ERROR_MESSAGE,
    );
    if (!wrapper || !text) return;
    text.textContent = '';
    wrapper.hidden = true;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'kd-cart-add-form': KdCartAddForm;
  }
}
